// React Dependencies
import React, { useEffect } from 'react';

// External Dependencies
import { makeStyles } from '@material-ui/core/styles';
import {
  Grid,
  Card,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Button,
  Divider,
  TextField,
  InputAdornment,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';

import PropTypes from 'prop-types';

const useStyles = makeStyles((theme) => ({
  root: {
    margin: 'auto',
  },
  cardHeader: {
    padding: theme.spacing(1, 2),
  },
  list: {
    width:           300,
    height:          230,
    backgroundColor: theme.palette.background.paper,
    overflow:        'auto',
  },
  button: {
    margin: theme.spacing(0.5, 0),
  },
}));

function not(a, b) {
  return a.filter((value) => b.indexOf(value) === -1);
}

function intersection(a, b) {
  return a.filter((value) => b.indexOf(value) !== -1);
}

function union(a, b) {
  return [...a, ...not(b, a)];
}

const TagTransferList = ({ availableTags, selectedTags, onChangeTags }) => {
  const classes = useStyles();
  const [checked, setChecked] = React.useState([]);
  const [left, setLeft] = React.useState([]);
  const [leftCopy, setLeftCopy] = React.useState([]);
  const [right, setRight] = React.useState([]);
  const [rightCopy, setRightCopy] = React.useState([]);
  const [searchValue, setSearchValue] = React.useState('');

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  useEffect(() => {
    // left is available tags that are not in selected
    const filteredAvailable = availableTags.filter((tag) => !selectedTags.includes(tag));

    setLeft(filteredAvailable);
    setLeftCopy(filteredAvailable);

    setRight(selectedTags);
    setRightCopy(selectedTags);
  }, [availableTags, selectedTags]);

  const handleToggle = (value) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    setChecked(newChecked);
  };

  const numberOfChecked = (items) => intersection(checked, items).length;

  const handleToggleAll = (items) => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked(not(checked, items));
    } else {
      setChecked(union(checked, items));
    }
  };

  const handleCheckedRight = () => {
    setSearchValue('');
    const newRight = rightCopy.concat(leftChecked);
    setRight(newRight);
    setRightCopy(newRight);
    setLeft(not(leftCopy, leftChecked));
    setLeftCopy(not(leftCopy, leftChecked));
    setChecked(not(checked, leftChecked));
    onChangeTags(newRight);
  };

  const handleCheckedLeft = () => {
    setSearchValue('');
    const newRight = not(rightCopy, rightChecked);
    setLeft(leftCopy.concat(rightChecked));
    setLeftCopy(leftCopy.concat(rightChecked));
    setRight(newRight);
    setRightCopy(newRight);
    setChecked(not(checked, rightChecked));
    onChangeTags(newRight);
  };

  const customList = (title, items) => (
    <Card>
      <CardHeader
        className={classes.cardHeader}
        avatar={(
          <Checkbox
            color="primary"
            onClick={handleToggleAll(items)}
            checked={numberOfChecked(items) === items.length && items.length !== 0}
            indeterminate={numberOfChecked(items) !== items.length && numberOfChecked(items) !== 0}
            disabled={items.length === 0}
            inputProps={{ 'aria-label': 'All Items Selected' }}
          />
        )}
        title={title}
        subheader={`${numberOfChecked(items)}/${items.length} Selected`}
      />
      <Divider />
      <List className={classes.list} dense component="div" role="list">
        {items.map((value) => {
          const labelId = `transfer-list-item-${value}-label`;
          return (
            <ListItem key={value} role="listitem" button onClick={handleToggle(value)}>
              <ListItemIcon>
                <Checkbox
                  color="primary"
                  checked={checked.indexOf(value) !== -1}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{ 'aria-labelledby': labelId }}
                />
              </ListItemIcon>
              <ListItemText
                id={labelId}
                primary={value}
              />
            </ListItem>
          );
        })}
      </List>
    </Card>
  );

  const onChangeSearch = (e) => {
    setSearchValue(e.target.value);
    if (e.target.value) {
      const q = e.target.value.toLowerCase();
      const leftArr = leftCopy.filter((tag) => tag.toLowerCase().includes(q));
      setLeft(leftArr);

      const rightArr = rightCopy.filter((tag) => tag.toLowerCase().includes(q));
      setRight(rightArr);
    } else {
      setLeft(leftCopy);
      setRight(rightCopy);
    }
  };

  return (
    <div>
      <TextField
        id="input-with-icon-textfield"
        placeholder="Search Tags..."
        autoComplete="off"
        onChange={(e) => onChangeSearch(e)}
        value={searchValue}
        style={{ marginTop: 10, marginBottom: 10, width: '100%' }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      <Grid container justify="space-between" alignItems="center" className={classes.root}>
        <Grid item xs={12} md={5}>{customList('Available Tags', left)}</Grid>
        <Grid item xs={12} md={2}>
          <Grid container direction="column" alignItems="center">
            <Button
              variant="outlined"
              size="small"
              className={classes.button}
              onClick={handleCheckedRight}
              disabled={leftChecked.length === 0}
              aria-label="move selected right"
            >
              &gt;
            </Button>
            <Button
              variant="outlined"
              size="small"
              className={classes.button}
              onClick={handleCheckedLeft}
              disabled={rightChecked.length === 0}
              aria-label="move selected left"
            >
              &lt;
            </Button>
          </Grid>
        </Grid>
        <Grid item xs={12} md={5}>{customList('Tags in Group', right)}</Grid>
      </Grid>
    </div>
  );
};

TagTransferList.propTypes = {
  availableTags: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedTags:  PropTypes.arrayOf(PropTypes.string).isRequired,
  onChangeTags:  PropTypes.func.isRequired,
};

export default TagTransferList;
